import { apiRequest, ApiClientError } from '@/lib/api/client'

export type RoulettePrizeType = 'coupon' | 'lucky_number' | 'pix' | 'none'
export type DonorPrizeStatus = 'pending' | 'used' | 'paid' | 'no_prize'

export const DONOR_PRIZE_STATUS_LABELS: Record<DonorPrizeStatus, string> = {
  pending: 'Pendente',
  used: 'Utilizado',
  paid: 'Pago',
  no_prize: 'Sem prêmio',
}

export const DONOR_PRIZE_STATUS_OPTIONS: { value: DonorPrizeStatus; label: string }[] = (
  Object.keys(DONOR_PRIZE_STATUS_LABELS) as DonorPrizeStatus[]
).map((status) => ({
  value: status,
  label: DONOR_PRIZE_STATUS_LABELS[status],
}))

export function displayDonorPrizeStatus(
  prize: Pick<DonorPrize, 'status' | 'prize_type'>
): string {
  if (prize.prize_type === 'none') {
    return DONOR_PRIZE_STATUS_LABELS.no_prize
  }

  if (prize.prize_type === 'lucky_number' && prize.status === 'used') return 'Entregue'
  return DONOR_PRIZE_STATUS_LABELS[prize.status]
}

export interface RouletteConfig {
  id: number
  campaign_id: number
  is_active: boolean
  positions: number
  spins_per_share: number
  amount_per_spin: number | null
  spins_per_amount: number
  created_at: string
  updated_at: string
}

export interface CouponPrizePayload {
  description?: string
  discount_percent?: number
  discount_amount?: number
  min_purchase?: number
  valid_until?: string
}

export interface LuckyNumberPrizePayload {
  quantity?: number
  lucky_numbers?: string[]
}

export interface PixPrizePayload {
  amount?: number
}

export interface NonePrizePayload {
  label?: string
}

export type RoulettePrizePayload =
  | CouponPrizePayload
  | LuckyNumberPrizePayload
  | PixPrizePayload
  | NonePrizePayload

export interface RoulettePrize {
  id: number
  campaign_id: number
  position: number
  prize_type: RoulettePrizeType
  probability: number
  prize_payload: RoulettePrizePayload | null
  created_at: string
  updated_at: string
}

export interface DonorRef {
  id: number
  name: string
  email: string
  phone?: string | null
}

export interface DonorPrize {
  id: number
  donor_id: number
  campaign_id: number
  prize_type: RoulettePrizeType
  prize_payload: Record<string, unknown> | null
  status: DonorPrizeStatus
  created_at: string
  updated_at: string
  donor?: DonorRef
}

export interface RouletteConfigInput {
  is_active?: boolean
  positions?: number
  spins_per_share?: number
  amount_per_spin?: number | null
  spins_per_amount?: number
}

export interface RoulettePrizeSlotInput {
  position: number
  prize_type: RoulettePrizeType
  probability: number
  prize_payload?: RoulettePrizePayload | null
}

export interface StoreRoulettePrizesInput {
  prizes: RoulettePrizeSlotInput[]
}

type ConfigResponse = { config: RouletteConfig }
type PrizesResponse = { prizes: RoulettePrize[] }
export interface RouletteDistributedPrizesPagination {
  current_page: number
  per_page: number
  total: number
  last_page: number
}

export interface DistributedPrizesResult {
  prizes: DonorPrize[]
  pagination: RouletteDistributedPrizesPagination
}

type DistributedResponse = {
  prizes: DonorPrize[]
  pagination: RouletteDistributedPrizesPagination
}

export {
  probabilitiesSumValid,
  sumRouletteProbabilitiesPercent,
} from '@/lib/rouletteProbability'

/**
 * Busca a config da roleta da campanha.
 * Retorna null se não existir (404) ou se o backend não suportar GET (405).
 * Não lança nesses casos, para a aba abrir normalmente.
 */
export async function getRouletteConfig(
  campaignId: number,
  token?: string | null
): Promise<RouletteConfig | null> {
  try {
    const data = await apiRequest<ConfigResponse>(
      `/api/campaigns/${campaignId}/roulette/config`,
      { method: 'GET', token: token ?? undefined }
    )
    return data.config ?? null
  } catch (err) {
    if (err instanceof ApiClientError && (err.status === 404 || err.status === 405)) {
      return null
    }
    throw err
  }
}

/**
 * Cria ou atualiza a config da roleta (POST). Idempotente no backend.
 */
export async function createOrUpdateRouletteConfig(
  campaignId: number,
  body: RouletteConfigInput,
  token?: string | null
): Promise<RouletteConfig> {
  const data = await apiRequest<ConfigResponse>(
    `/api/campaigns/${campaignId}/roulette/config`,
    {
      method: 'POST',
      body: JSON.stringify(body),
      token: token ?? undefined,
    }
  )
  if (!data.config) {
    throw new Error('Resposta da API sem config.')
  }
  return data.config
}

/**
 * Atualiza a config da roleta (PUT). Exige config já existente.
 */
export async function updateRouletteConfig(
  campaignId: number,
  body: RouletteConfigInput,
  token?: string | null
): Promise<RouletteConfig> {
  const data = await apiRequest<ConfigResponse>(
    `/api/campaigns/${campaignId}/roulette/config`,
    {
      method: 'PUT',
      body: JSON.stringify(body),
      token: token ?? undefined,
    }
  )
  if (!data.config) {
    throw new Error('Resposta da API sem config.')
  }
  return data.config
}

/**
 * Lista os slots de prêmios (config) da roleta.
 */
export async function getRoulettePrizes(
  campaignId: number,
  token?: string | null
): Promise<RoulettePrize[]> {
  const data = await apiRequest<PrizesResponse>(
    `/api/campaigns/${campaignId}/roulette/prizes`,
    { method: 'GET', token: token ?? undefined }
  )
  return data.prizes ?? []
}

/**
 * Define ou substitui todos os prêmios da roleta (POST).
 * probability em porcentagem (0–100); soma deve ser 100.
 */
export async function setRoulettePrizes(
  campaignId: number,
  body: StoreRoulettePrizesInput,
  token?: string | null
): Promise<RoulettePrize[]> {
  const data = await apiRequest<PrizesResponse>(
    `/api/campaigns/${campaignId}/roulette/prizes`,
    {
      method: 'POST',
      body: JSON.stringify(body),
      token: token ?? undefined,
    }
  )
  return data.prizes ?? []
}

/**
 * Atualiza (substitui) todos os prêmios da roleta (PUT).
 */
export async function updateRoulettePrizes(
  campaignId: number,
  body: StoreRoulettePrizesInput,
  token?: string | null
): Promise<RoulettePrize[]> {
  const data = await apiRequest<PrizesResponse>(
    `/api/campaigns/${campaignId}/roulette/prizes`,
    {
      method: 'PUT',
      body: JSON.stringify(body),
      token: token ?? undefined,
    }
  )
  return data.prizes ?? []
}

export const DISTRIBUTED_PRIZES_PER_PAGE = 20

export interface DistributedPrizesQueryParams {
  page?: number
  per_page?: number
  status?: DonorPrizeStatus
  prize_type?: RoulettePrizeType
  email?: string
  date_from?: string
  date_to?: string
}

/**
 * Lista prêmios já distribuídos aos compradores (auditoria), com paginação e filtros.
 */
export async function getDistributedPrizes(
  campaignId: number,
  params: DistributedPrizesQueryParams = {},
  token?: string | null
): Promise<DistributedPrizesResult> {
  const searchParams = new URLSearchParams()
  if (params.page != null) searchParams.set('page', String(params.page))
  if (params.per_page != null) searchParams.set('per_page', String(params.per_page))
  if (params.status) searchParams.set('status', params.status)
  if (params.prize_type) searchParams.set('prize_type', params.prize_type)
  const email = params.email?.trim()
  if (email) searchParams.set('email', email)
  if (params.date_from) searchParams.set('date_from', params.date_from)
  if (params.date_to) searchParams.set('date_to', params.date_to)
  const query = searchParams.toString()
  const path = `/api/campaigns/${campaignId}/roulette/prizes/distributed${query ? `?${query}` : ''}`

  const data = await apiRequest<DistributedResponse>(path, {
    method: 'GET',
    token: token ?? undefined,
  })

  if (!data.pagination) {
    throw new Error('Resposta da API sem paginação.')
  }

  return {
    prizes: data.prizes ?? [],
    pagination: data.pagination,
  }
}

export interface UpdateDistributedPrizeStatusInput {
  status: DonorPrizeStatus
}

export interface RouletteWinnerShareCardCampaign {
  title: string
  logo_url: string | null
  primary_color: string | null
  button_color: string | null
}

export interface RouletteWinnerShareCardWinner {
  display_name: string
  display_phone: string | null
}

export interface RouletteWinnerShareCardPrize {
  prize_type: RoulettePrizeType
  headline: string
  detail: string | null
}

export interface RouletteWinnerShareCardRoulettePrize {
  position: number
  prize_type: RoulettePrizeType
  display: Record<string, unknown>
}

export interface RouletteWinnerShareCardRoulette {
  positions: number
  prizes: RouletteWinnerShareCardRoulettePrize[]
}

export interface RouletteWinnerShareCard {
  campaign: RouletteWinnerShareCardCampaign
  winner: RouletteWinnerShareCardWinner
  prize: RouletteWinnerShareCardPrize
  roulette: RouletteWinnerShareCardRoulette | null
}

type ShareCardResponse = { share_card: RouletteWinnerShareCard }

type UpdateDistributedPrizeStatusResponse = { prize: DonorPrize }

/**
 * Atualiza manualmente o status de um prêmio distribuído (salvamento imediato).
 */
export async function updateDistributedPrizeStatus(
  campaignId: number,
  prizeId: number,
  body: UpdateDistributedPrizeStatusInput,
  token?: string | null
): Promise<DonorPrize> {
  const data = await apiRequest<UpdateDistributedPrizeStatusResponse>(
    `/api/campaigns/${campaignId}/roulette/prizes/distributed/${prizeId}`,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
      token: token ?? undefined,
    }
  )

  if (!data.prize) {
    throw new Error('Resposta da API sem prêmio atualizado.')
  }

  return data.prize
}

/**
 * Busca payload para gerar PNG de compartilhamento de ganhador da roleta (dados mascarados).
 */
export async function getRouletteWinnerShareCard(
  campaignId: number,
  prizeId: number,
  token?: string | null
): Promise<RouletteWinnerShareCard> {
  const data = await apiRequest<ShareCardResponse>(
    `/api/campaigns/${campaignId}/roulette/prizes/distributed/${prizeId}/share-card`,
    {
      method: 'GET',
      token: token ?? undefined,
    }
  )

  if (!data.share_card) {
    throw new Error('Resposta da API sem dados do card de compartilhamento.')
  }

  return data.share_card
}
